import get from 'lodash-es/get'
import { requestAirflow, receiveAirflow } from 'store/objects'
import { clearStatus, setLoadingStatus } from 'store/status'
import airVelocityWorker from './airvelocity.worker?worker'
import { evalPointSpacing, isExteriorWall, isInteriorWall, isValidSegment, nearestValueFromArray, rotate } from './util'
import Units from 'components/DrawingCanvas/lib/units'
import client from '~/client'
import { GET_AIR_VELOCITIES_QUERY } from '~/client/queries'
import { AppDispatch } from '~/store'
import { ObjectsState } from '~/store/objects/reducer'
import { ProductVariationAirVelocitiesQuery } from '~/gql/graphql'
import { Airflow, Obstruction, Wall, WallSegment } from '~/store/objects/types'
import { computeDirectionalFanNudge } from '~/store/objects/productNudge'
import { Vector2Like, Vector3Like } from 'three'

export const DEFAULT_COOLING_FAN_SPEED = 'COOLING'
export const DEFAULT_DESTRAT_FAN_SPEED = 'DESTRATIFICATION'
export const AIRFLOW_EVALUATION_POINTS = [4, 24, 43, 67]

async function airflowUtil(
  {
    airVelocities,
    objects,
    obstructions,
    segments,
  }: {
    airVelocities: VariationVelocities[]
    objects: Record<string, Wall>
    obstructions: Record<string, Obstruction>
    segments: Record<string, WallSegment>
  },
  callback: (result: Airflow[]) => void
) {
  try {
    // Valid segments that can be used to make walls
    const validSegments = Object.values(segments).filter(isValidSegment)
    // Each time we need to iterate through segments use this function
    const mapSegments = (parentId: string, addSegment: (segment: WallSegment) => void) => {
      for (let i = 0; i < validSegments.length; i++) {
        const segment = validSegments[i]
        if (segment.parentId === parentId) addSegment(segment)
      }
    }

    // Polygon needed to see if obstructions and walls are inside
    const exteriorPolygon = []
    // Fields needed for the input for the air velocity package
    const ceilingFans: {
      interpolationPointsId: string
      position: Vector3Like
      rotation: Vector3Like
      diameter: number
    }[] = []
    const directionalFans: {
      interpolationPointsId: string
      position: Vector3Like
      rotation: Vector3Like
      diameter: number
    }[] = []
    const interiorWalls: [Vector3Like, Vector3Like][] = []
    const perimeterWalls: { vertex1: Vector3Like; vertex2: Vector3Like }[] = []
    // Take all the wall values out of objects
    const walls = Object.values(objects)
    // Find the exteriorWall
    const exteriorWall = walls.find(isExteriorWall)!

    // Set the values for the perimeter walls and exterior
    const exteriorWallId = exteriorWall.id

    // Go through the valid segments and return the vertices of the exterior Walls
    mapSegments(exteriorWallId, segment => {
      // Take the first startpoint and all the endpoints to form the polygon
      // of the exterior walls
      if (!exteriorPolygon.length) {
        exteriorPolygon.push([segment.startPoint.x, segment.startPoint.y])
      }
      exteriorPolygon.push([segment.endPoint.x, segment.endPoint.y])
      // perimeter walls to pass to the air velocity package
      perimeterWalls.push({
        vertex1: { ...segment.startPoint },
        vertex2: { ...segment.endPoint },
      })
    })

    // Iterate over all the interior walls
    for (let i = 0; i < walls.length; i++) {
      // Determine the object we are inspecting is an interior Wall
      if (isInteriorWall(walls[i])) {
        // Pull the interior wall info out of segments
        mapSegments(walls[i].id, segment => {
          const startPoint = get(segment, 'startPoint')
          const endPoint = get(segment, 'endPoint')
          if (startPoint && endPoint) {
            interiorWalls.push([segment.startPoint, segment.endPoint])
          }
        })
      }
    }

    // Taking the value of each of the obstructions
    const obs = Object.values(obstructions)
    const obstructs: { height: number; positions: Vector2Like[]; startHeight: number }[] = []
    // Iterate over the obstructions
    for (let i = 0; i < obs.length; i++) {
      const { height, obstructionType, offset, position: pos, positions, rotation, startLocation } = obs[i]
      // Making sure there is a base position
      const position = pos || { x: 0, y: 0 }
      // rotation of the object pulled out
      const rot = (rotation && rotation.z) || 0
      // Clone the positions so the obstruction keeps them
      const clonedPositions = positions.map(pos => {
        const [x, y] = rotate(position.x, position.y, pos.x, pos.y, -rot)
        return {
          x,
          y,
        }
      })

      // If the obstruction is basic, remove the last element b/c repeated
      if (obstructionType === 'basic') clonedPositions.pop()

      // Start height is in case the obstruction starts off the ground
      let startHeight = offset || 0
      if (startLocation === 'ceiling') {
        startHeight = segments[get(exteriorWall, 'segments.0')].height - height || 0
      }
      // Minimal description of the obstructions for the AVP
      // Make sure to use clonedPositions to not delete positions
      // And make sure there are actually positions in the array
      if (clonedPositions.length) {
        obstructs.push({
          height: Units.inchesToNative(height),
          positions: clonedPositions,
          startHeight,
        })
      }
    }

    // The ids and angles of all fans in the scene, used to filter air velocities
    const interpolationPoints: Record<
      number,
      Record<
        string,
        {
          id: unknown
          interpolationPoints: {
            airVelocity: number
            distance: number
            offset: number | undefined
          }[]
        }
      >
    > = {}
    // Add keys for each evaluation point into the object
    AIRFLOW_EVALUATION_POINTS.forEach(value => (interpolationPoints[value] = {}))

    // Run through all the products and calculate the airvelocities needed
    // And push the product info into the appropriate array
    for (const variationVelocity of airVelocities) {
      const { size, airVelocities, position, rotation, product, id: variationId, coolingFanSpeedId } = variationVelocity
      const { type, fanSpeed } = product
      const height = position.z

      const isDirectional = type === 'DIRECTIONAL'

      // Power is the modifier of the airVelocity for calculating airflow
      const power = fanSpeed?.power ? fanSpeed?.power / 100 : 1

      // Conversion of angles from the SpecLab system to the airflow system
      const angle = rotation.x

      const heights: number[] = []
      // Determine the heights in the airVelocities
      for (const { airfoilHeight } of airVelocities!) {
        if (!heights.includes(airfoilHeight)) heights.push(airfoilHeight)
      }
      // Find the airfoilHeight on the Interpolation points closest to the fan
      const airfoilHeight = height != null && nearestValueFromArray(height, heights)
      // If height was undefined skip the rest, this should never happen
      if (airfoilHeight === false) continue

      // The interpolationPoints id changes based on the type of fan
      // Because we need to include the angle for directionals.
      const interpolationPointsId = isDirectional
        ? `${variationId}.${angle}.${coolingFanSpeedId || DEFAULT_COOLING_FAN_SPEED}.${airfoilHeight}`
        : `${variationId}.${coolingFanSpeedId || DEFAULT_COOLING_FAN_SPEED}.${airfoilHeight}`

      // Calculate the interpolation points if they don't already exist
      // Each set of points should have every eval height, so we just need to look at one
      if (interpolationPoints[AIRFLOW_EVALUATION_POINTS[0]][interpolationPointsId] == null) {
        // Decide which array to push into based on the type of fan
        // iterate over them grabbing the appropriate interpolation points
        for (const interpolationPoint of airVelocities!) {
          const {
            airVelocity,
            distanceFromFan: distance,
            evaluationHeight,
            offsetFromFanCenter: offset,
          } = interpolationPoint

          // Check if the airfoilHeight matches the one we want
          const pointHeight = get(interpolationPoint, 'airfoilHeight')
          const airfoilMismatch = pointHeight !== airfoilHeight

          // If there is an angle check that it matches that of the fan
          const angleMismatch = isDirectional && interpolationPoint.angle !== angle

          // Make sure that height is in AIRFLOW_EVALUATION_POINTS by
          // checking if the object exists on interpolationPoints
          const heightExists = interpolationPoints[evaluationHeight]

          if (airfoilMismatch || angleMismatch || !heightExists) continue

          // If no points have been added to this eval height, add one
          if (!interpolationPoints[evaluationHeight][interpolationPointsId]) {
            interpolationPoints[evaluationHeight][interpolationPointsId] = {
              id: interpolationPointsId,
              interpolationPoints: [],
            }
          }

          interpolationPoints[evaluationHeight][interpolationPointsId].interpolationPoints.push({
            airVelocity: airVelocity * power,
            distance: distance,
            offset: offset == null ? undefined : offset,
          })
        }
      }

      // If the InterpolationData is there, push the product into the array
      if (interpolationPoints[AIRFLOW_EVALUATION_POINTS[0]][interpolationPointsId]) {
        const array = isDirectional ? directionalFans : ceilingFans
        array.push({
          interpolationPointsId,
          position,
          rotation,
          diameter: size,
        })
      }
    }
    // Map each of the inputs to the evaluation heights
    const inputs = AIRFLOW_EVALUATION_POINTS.map(evalHeight => ({
      ceilingFans,
      directionalFans,
      evalHeight,
      evalPointSpacing,
      exteriorWallId,
      interiorWalls,
      perimeterWalls,
      interpolationPoints: Object.values(interpolationPoints[evalHeight]),
      obstructions: obstructs,
    }))
    // Build the service worker to calculate the airflow velocities
    const worker = new airVelocityWorker()
    worker.onmessage = event => {
      const avData: {
        airVelocities: number[][]
        topLeftAnchorPosition: Vector2Like
      }[] = event.data
      const airflow = avData.map((av, idx) => ({
        objectId: inputs[idx].exteriorWallId,
        gridSize: evalPointSpacing,
        evaluationHeight: inputs[idx].evalHeight,
        airflowVelocities: av.airVelocities,
        topLeftAnchorPosition: av.topLeftAnchorPosition,
      }))
      callback(airflow)
      worker.terminate()
    }
    // Send the inputs calculated into the service worker
    worker.postMessage(inputs)
  } catch (err) {
    console.log(err)
    return []
  }
}

type VariationVelocities = ProductVariationAirVelocitiesQuery['ProductVariation'] & {
  rotation: Vector3Like
  position: Vector3Like
  coolingFanSpeedId: string
}

export const updateAirflow = async (
  dispatch: AppDispatch,
  {
    products,
    objects,
    segments,
    obstructions,
  }: Pick<ObjectsState, 'products' | 'objects' | 'segments' | 'obstructions'>
) => {
  dispatch(setLoadingStatus())
  dispatch(requestAirflow())

  const variationAirVelocities = await Promise.all(
    Object.values(products).map<Promise<VariationVelocities>>(
      async ({ variationId, coolingFanSpeedId, position, rotation, isForcedWallMount, isDirectionalOverhead }) => {
        const { data } = await client.query({
          query: GET_AIR_VELOCITIES_QUERY,
          variables: {
            id: variationId,
            fanSpeedId: coolingFanSpeedId!,
            heightFromFloor: Math.round(position.z),
          },
        })
        const fanNudge = computeDirectionalFanNudge(
          { rotation, isForcedWallMount, isDirectionalOverhead },
          data.ProductVariation.size
        )
        const nudgedPosition: Vector3Like = {
          x: position.x + fanNudge.x,
          y: position.y + fanNudge.y,
          z: position.z + fanNudge.z,
        }
        return { ...data.ProductVariation, rotation, position: nudgedPosition, coolingFanSpeedId: coolingFanSpeedId! }
      }
    )
  )

  if (variationAirVelocities.length) {
    airflowUtil(
      {
        objects,
        segments,
        obstructions,
        airVelocities: variationAirVelocities,
      },
      airflow => {
        dispatch(clearStatus)
        dispatch(receiveAirflow(airflow))
      }
    )
  } else {
    // If there are no fans, there is no airflow
    dispatch(clearStatus)
    dispatch(receiveAirflow([]))
  }
}
